import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, HandHeart, Users, FolderHeart, FileText, Image as ImageIcon,
  Settings as SettingsIcon, CreditCard, UserCog, ScrollText, LogOut, Menu, X, KeyRound, Loader2, Mail,
} from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuth";
import { adminApi, apiError } from "@/admin/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "content_manager", "donation_manager"], end: true },
  { to: "/admin/donations", label: "Donations", icon: HandHeart, roles: ["donation_manager"] },
  { to: "/admin/donors", label: "Donors", icon: Users, roles: ["donation_manager"] },
  { to: "/admin/messages", label: "Messages", icon: Mail, roles: ["super_admin", "content_manager", "donation_manager"] },
  { to: "/admin/projects", label: "Projects & Causes", icon: FolderHeart, roles: ["content_manager"] },
  { to: "/admin/content", label: "Content (CMS)", icon: FileText, roles: ["content_manager"] },
  { to: "/admin/media", label: "Media Library", icon: ImageIcon, roles: ["content_manager"] },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, roles: ["content_manager", "super_admin"] },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, roles: ["super_admin"] },
  { to: "/admin/users", label: "Admin Users", icon: UserCog, roles: ["super_admin"] },
  { to: "/admin/activity", label: "Activity Log", icon: ScrollText, roles: ["super_admin"] },
];

const can = (user, roles) => user && (user.role === "super_admin" || roles.includes(user.role));

const ChangePasswordGate = ({ forced, onClose }) => {
  const { refresh } = useAdminAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      await refresh();
      if (onClose) onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="force-change-pw" className="w-full max-w-sm bg-white rounded-2xl p-8 relative">
        {!forced && <button type="button" onClick={onClose} className="absolute top-4 right-4 text-gray-400"><X size={18} /></button>}
        <div className="flex items-center gap-2 mb-1 text-[hsl(160_38%_16%)]"><KeyRound size={20} /><span className="font-serif text-2xl">{forced ? "Set a new password" : "Change password"}</span></div>
        <p className="text-sm text-gray-500 mb-5">{forced ? "For security, please change your initial password." : "Update your account password."}</p>
        <input data-testid="cp-current" type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)}
               className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm" />
        <input data-testid="cp-new" type="password" placeholder="New password (8+ chars, letter & number)" value={nw} onChange={(e) => setNw(e.target.value)}
               className="mb-5 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm" />
        <button data-testid="cp-submit" disabled={loading} className="w-full rounded-lg bg-[hsl(13_65%_54%)] text-white py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading && <Loader2 size={16} className="animate-spin" />} Update Password
        </button>
      </form>
    </div>
  );
};

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [mode, setMode] = useState(null);
  const items = NAV.filter((n) => can(user, n.roles));

  useEffect(() => { adminApi.get("/admin/mode").then((r) => setMode(r.data.mode)).catch(() => {}); }, []);

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed lg:static z-40 top-0 left-0 h-full w-64 bg-[hsl(160_38%_13%)] text-white flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-20 px-6 flex items-center justify-between border-b border-white/10">
          <div className="leading-none">
            <div className="font-serif text-2xl">Findivine</div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-[hsl(27_60%_67%)]">Admin Console</div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[hsl(13_65%_54%)] text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
                <Icon size={17} strokeWidth={1.75} /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-xs text-white/60 truncate">{user?.email}<div className="text-[10px] uppercase tracking-wider text-[hsl(27_60%_67%)]">{user?.role?.replace("_", " ")}</div></div>
          <button data-testid="change-pw-btn" onClick={() => setShowPw(true)} className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-white/80 hover:bg-white/10">
            <KeyRound size={16} /> Change password
          </button>
          <button data-testid="admin-logout" onClick={doLogout} className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-white/80 hover:bg-white/10">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center gap-3 px-4 lg:px-8 sticky top-0 z-20">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-toggle"><Menu size={22} /></button>
          <div className="font-semibold text-gray-800">{(items.find((n) => n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)) || {}).label || "Dashboard"}</div>
          <div className="ml-auto flex items-center gap-2">
            {mode && (
              <span data-testid="mode-badge" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${mode === "live" ? "bg-emerald-500" : "bg-amber-500"}`} />
                {mode} Mode
              </span>
            )}
          </div>
        </header>
        <main className="p-4 lg:p-8"><Outlet /></main>
      </div>

      {user?.must_change_password && <ChangePasswordGate forced />}
      {showPw && !user?.must_change_password && <ChangePasswordGate onClose={() => setShowPw(false)} />}
    </div>
  );
}
